import { Grid2 as Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DrilldownItem from '@components/DrilldownItem';
import {
  FormikDropdownField,
  FormikFhirDateField,
  FormikTextField,
} from '@components/FormikFields';
import PhoneNumbersField from '@components/PhoneNumbersField';

import { useCountryRegion } from '@hooks/useCountryRegion';
import { useCuration } from '@hooks/useCuration';

import type { PatientRecord } from '@models/patient';

import { capitalize } from '@utils/format';

import { Gender } from 'generated/types';
import type { GetUserRecordQuery } from 'generated/user.generated';

import type { FormValues } from './Card';

const messages = defineMessages({
  firstName: {
    id: 'GLOBAL.firstName',
    defaultMessage: 'First Name',
  },
  middleName: {
    id: 'GLOBAL.middleName',
    defaultMessage: 'Middle Name',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
  sex: {
    id: 'clientProfileCard.sex',
    defaultMessage: 'Sex',
  },
  gender: {
    id: 'clientProfileCard.gender',
    defaultMessage: 'Gender',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  primaryAddress: {
    id: 'GLOBAL.primaryAddress',
    defaultMessage: 'Primary Address',
  },
  secondaryAddress: {
    id: 'GLOBAL.secondaryAddress',
    defaultMessage: 'Secondary Address',
  },
  suite: {
    id: 'GLOBAL.suite',
    defaultMessage: 'Suite/Apartment',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  group: {
    id: 'GLOBAL.group',
    defaultMessage: 'Group',
  },
});

interface DemographicsFormProps {
  canEditName: boolean;
  patient: PatientRecord | null;
  user?: GetUserRecordQuery['getUserRecord'];
}

export default function DemographicsForm({
  canEditName,
  patient,
  user,
}: DemographicsFormProps) {
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const {
    state: { isEditing },
  } = useCuration();
  const { palette } = useTheme();

  const age = patient?.getAge();

  const { countryOptions, getProvinceOptions } = useCountryRegion();

  return (
    <>
      {isEditing && canEditName && (
        <>
          <Grid>
            <FormikTextField
              charLimit={50}
              label={formatMessage(messages.firstName)}
              name="first_name"
              required
            />
          </Grid>
          <Grid>
            <FormikTextField
              charLimit={50}
              label={formatMessage(messages.middleName)}
              name="middle_name"
            />
          </Grid>
          <Grid>
            <FormikTextField
              charLimit={50}
              label={formatMessage(messages.lastName)}
              name="last_name"
              required
            />
          </Grid>
        </>
      )}
      <Grid>
        {isEditing ? (
          <FormikFhirDateField
            formats={['date']}
            label={formatMessage(messages.dob)}
            name="birthday"
            onChange={(value) => {
              setFieldValue('birthday', value);
            }}
            required
            value={values.birthday}
          />
        ) : (
          <DrilldownItem
            label={formatMessage(messages.dob)}
            readOnly
            value={`${patient?.getBirthday()} ${
              age ? `(${age} years old)` : ''
            }`}
          />
        )}
      </Grid>
      <Grid>
        <DrilldownItem
          label={formatMessage(messages.sex)}
          labelColor="primary.main"
          options={Object.entries(Gender).map(([key, value]) => ({
            k: value,
            v: key,
          }))}
          name="sex"
          type="dropdown"
          value={values.sex ? capitalize(values.sex) : null}
        />
      </Grid>
      <Grid>
        <DrilldownItem
          label={formatMessage(messages.gender)}
          name="gender"
          value={values.gender ? capitalize(values.gender) : null}
        />
      </Grid>
      {!isEditing && (
        <>
          <Grid>
            <DrilldownItem
              label={formatMessage(messages.email)}
              readOnly
              value={patient?.email}
            />
          </Grid>
          <Grid>
            <DrilldownItem
              label={formatMessage(messages.group)}
              readOnly
              value={user?.group_name}
            />
          </Grid>
        </>
      )}

      <Grid>
        <PhoneNumbersField isEditing={isEditing} />
      </Grid>
      {isEditing ? (
        <>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.primaryAddress)}
              name="address"
            />
          </Grid>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.suite)}
              name="address2"
            />
          </Grid>
          <Grid>
            <FormikDropdownField
              label={formatMessage(messages.country)}
              labelColor={palette.primary.main}
              name="country"
              onChange={(value) => {
                setFieldValue('country', value);
                setFieldValue('region', '');
              }}
              options={countryOptions}
              required
            />
          </Grid>
          <Grid>
            <FormikDropdownField
              label={formatMessage(messages.region)}
              labelColor={palette.primary.main}
              name="region"
              onChange={(value) => {
                setFieldValue('region', value);
              }}
              options={getProvinceOptions(values.country)}
              required
            />
          </Grid>
          <Grid>
            <FormikTextField label={formatMessage(messages.city)} name="city" />
          </Grid>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.postalCode)}
              name="postalCode"
            />
          </Grid>
        </>
      ) : (
        <Grid size={{ xs: 12, sm: 6 }}>
          <DrilldownItem
            label={formatMessage(messages.primaryAddress)}
            readOnly
            value={patient?.primaryAddress}
          />
        </Grid>
      )}
      {isEditing ? (
        <>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.secondaryAddress)}
              name="secondAddress"
            />
          </Grid>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.suite)}
              name="secondAddressUnit"
            />
          </Grid>
          <Grid>
            <FormikDropdownField
              label={formatMessage(messages.country)}
              labelColor={palette.primary.main}
              name="secondCountry"
              onChange={(value) => {
                setFieldValue('secondCountry', value);
                setFieldValue('secondRegion', '');
              }}
              options={countryOptions}
              required
            />
          </Grid>
          <Grid>
            <FormikDropdownField
              label={formatMessage(messages.region)}
              labelColor={palette.primary.main}
              name="secondRegion"
              onChange={(value) => {
                setFieldValue('secondRegion', value);
              }}
              options={getProvinceOptions(values.secondCountry)}
              required
            />
          </Grid>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.city)}
              name="secondCity"
            />
          </Grid>
          <Grid>
            <FormikTextField
              label={formatMessage(messages.postalCode)}
              name="secondPostalCode"
            />
          </Grid>
        </>
      ) : (
        <Grid size={{ xs: 12, sm: 6 }}>
          <DrilldownItem
            label={formatMessage(messages.secondaryAddress)}
            readOnly
            value={patient?.secondaryAddress}
          />
        </Grid>
      )}
    </>
  );
}
